// Centralized SEO configurations for all pages
// This makes it easy to manage and scale SEO across tons of pages

const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

export const seoConfigs = {
  home: {
    title: "TherapyList - Find a Therapist Near You",
    description: "Find licensed therapists in your area with TherapyList. Search by location, specialty, and insurance to connect with the right mental health professional for your needs.",
    keywords: "find therapist near me, therapist near me, mental health, counseling, psychology, licensed therapist, therapy directory",
    canonical: baseUrl,
    ogTitle: "TherapyList - Find a Therapist Near You",
    ogDescription: "Find licensed therapists in your area. Search by location, specialty, and insurance to connect with the right mental health professional."
  },
  
  blog: {
    title: "Mental Health Blog | TherapyList",
    description: "Expert insights, resources, and guidance for mental health professionals and those seeking wellness. Stay informed with the latest research and techniques.",
    keywords: "mental health blog, therapy tips, mental wellness, therapy insights, mental health resources, therapy guidance",
    canonical: `${baseUrl}/blog`,
    ogTitle: "TherapyList Mental Health Blog",
    ogDescription: "Expert insights and resources for mental health professionals and wellness seekers."
  },
  
  findTherapists: {
    title: "Find Therapists Near You | TherapyList",
    description: "Search and connect with licensed therapists in your area. Filter by specialties, insurance, location, and more to find the perfect mental health professional.",
    keywords: "find therapist, therapist near me, mental health professional, licensed therapist, therapy search, counselor",
    canonical: `${baseUrl}/find-therapists`,
    ogTitle: "Find Licensed Therapists Near You",
    ogDescription: "Search and connect with licensed therapists in your area. Filter by specialties, insurance, and location."
  },
  
  forTherapists: {
    title: "Join TherapyList - For Mental Health Professionals",
    description: "Join TherapyList to connect with clients seeking mental health services. Grow your practice with our therapist directory platform.",
    keywords: "therapist directory, mental health professional, grow therapy practice, therapist marketing, client referrals",
    canonical: `${baseUrl}/for-therapists`,
    ogTitle: "Grow Your Therapy Practice with TherapyList",
    ogDescription: "Connect with clients seeking mental health services. Join our directory of licensed professionals."
  },

  login: {
    title: "TherapyList Login | Access Your Account",
    description: "Sign in to your TherapyList account to manage your therapist profile, view client inquiries, and grow your mental health practice.",
    keywords: "therapist login, mental health professional login, therapy practice account, therapist dashboard access",
    canonical: `${baseUrl}/login`,
    ogTitle: "TherapyList Login - Access Your Account",
    ogDescription: "Sign in to manage your therapist profile and connect with clients seeking mental health services."
  },
  
  about: {
    title: "About TherapyList - Our Mission to Connect People with Mental Health Care",
    description: "Learn about TherapyList's mission to make mental health care more accessible by connecting people with licensed therapists in their area.",
    keywords: "about therapy list, mental health mission, therapy access, mental health care, company story",
    canonical: `${baseUrl}/about`,
    ogTitle: "About TherapyList - Making Mental Health Care Accessible",
    ogDescription: "Learn about our mission to connect people with licensed therapists and make mental health care more accessible."
  },

  howItWorks: {
    title: "How TherapyList Works - Find Your Perfect Therapist in 3 Easy Steps",
    description: "Discover how TherapyList connects you with licensed therapists. Learn our simple 3-step process to find, connect, and start therapy with the right mental health professional.",
    keywords: "how therapy list works, find therapist process, therapy matching, mental health platform, therapist search guide",
    canonical: `${baseUrl}/how-it-works`,
    ogTitle: "How TherapyList Works - Simple Therapist Matching",
    ogDescription: "Learn our simple 3-step process to find, connect, and start therapy with licensed mental health professionals."
  },

  insurance: {
    title: "Insurance Coverage for Therapy | TherapyList",
    description: "Find therapists who accept your insurance. Learn about mental health coverage, in-network providers, and how to maximize your therapy benefits with TherapyList.",
    keywords: "therapy insurance coverage, mental health insurance, in network therapists, therapy benefits, insurance accepted therapists",
    canonical: `${baseUrl}/insurance`,
    ogTitle: "Find Therapists Who Accept Your Insurance",
    ogDescription: "Find in-network therapists and learn about mental health coverage options to make therapy more affordable."
  },

  specialties: {
    title: "Therapy Specialties | Find Specialized Mental Health Care | TherapyList",
    description: "Explore therapy specialties including anxiety, depression, trauma, couples counseling, ADHD, and more. Find therapists specialized in your specific needs.",
    keywords: "therapy specialties, anxiety therapy, depression therapy, trauma therapy, couples counseling, ADHD therapy, mental health specializations",
    canonical: `${baseUrl}/specialties`,
    ogTitle: "Therapy Specialties - Find Specialized Mental Health Care",
    ogDescription: "Explore therapy specialties and find mental health professionals specialized in your specific needs and concerns."
  },

  pricing: {
    title: "Therapy Pricing & Cost Information | TherapyList",
    description: "Learn about therapy costs, session rates, insurance coverage, and affordable mental health options. Find transparent pricing from licensed therapists.",
    keywords: "therapy cost, therapy pricing, session rates, mental health costs, affordable therapy, therapy fees",
    canonical: `${baseUrl}/pricing`,
    ogTitle: "Therapy Pricing & Cost Information",
    ogDescription: "Transparent pricing information for therapy sessions, insurance coverage, and affordable mental health options."
  },

  resources: {
    title: "Mental Health Resources & Therapy Guides | TherapyList",
    description: "Access free mental health resources, therapy guides, self-help tools, and educational content to support your mental wellness journey.",
    keywords: "mental health resources, therapy guides, self help tools, mental wellness, therapy tips, mental health education",
    canonical: `${baseUrl}/resources`,
    ogTitle: "Free Mental Health Resources & Therapy Guides",
    ogDescription: "Access free mental health resources, guides, and tools to support your mental wellness journey."
  },

  therapistSupport: {
    title: "Support for Therapists | Practice Growth & Resources | TherapyList",
    description: "Get support growing your therapy practice with TherapyList. Access resources, tools, and guidance for mental health professionals.",
    keywords: "therapist support, practice growth, therapist resources, mental health professional tools, therapy practice management",
    canonical: `${baseUrl}/therapist-support`,
    ogTitle: "Support for Mental Health Professionals",
    ogDescription: "Access resources, tools, and support to help grow your therapy practice with TherapyList."
  },

  contact: {
    title: "Contact TherapyList | Get Support & Ask Questions",
    description: "Contact TherapyList for support, questions, or feedback. We're here to help you find the right mental health professional or grow your practice.",
    keywords: "contact therapy list, customer support, help center, mental health support, therapist support contact",
    canonical: `${baseUrl}/contact`,
    ogTitle: "Contact TherapyList - We're Here to Help",
    ogDescription: "Get in touch with TherapyList for support, questions, or feedback. We're here to help with your mental health journey."
  },

  privacy: {
    title: "Privacy Policy | TherapyList",
    description: "Read TherapyList's privacy policy to understand how we protect your personal information and maintain your privacy in our mental health platform.",
    keywords: "privacy policy, data protection, personal information, mental health privacy, HIPAA compliance, user privacy",
    canonical: `${baseUrl}/privacy`,
    ogTitle: "TherapyList Privacy Policy",
    ogDescription: "Learn how TherapyList protects your personal information and maintains privacy in our mental health platform."
  },

  terms: {
    title: "Terms of Service | TherapyList",
    description: "Read TherapyList's terms of service to understand the rules and guidelines for using our mental health therapist directory platform.",
    keywords: "terms of service, user agreement, platform terms, therapist directory terms, mental health platform guidelines",
    canonical: `${baseUrl}/terms`,
    ogTitle: "TherapyList Terms of Service",
    ogDescription: "Understand the terms and conditions for using TherapyList's mental health therapist directory platform."
  }
};

// Helper function to generate dynamic SEO configs for therapist profiles, city pages, etc.
export const generateTherapistSEO = (therapist) => ({
  title: `${therapist.name} - ${therapist.specialty} Therapist | TherapyList`,
  description: `Book an appointment with ${therapist.name}, a licensed ${therapist.specialty} therapist in ${therapist.city}. ${therapist.bio?.substring(0, 150)}...`,
  keywords: `${therapist.name}, ${therapist.specialty}, therapist, ${therapist.city}, mental health, therapy`,
  canonical: `${baseUrl}/therapist/${therapist.slug}`,
  ogTitle: `${therapist.name} - Licensed ${therapist.specialty} Therapist`,
  ogDescription: `Connect with ${therapist.name} for professional ${therapist.specialty} therapy in ${therapist.city}.`,
  ogImage: therapist.profileImage || '/logo512.png'
});

export const generateCitySEO = (city, state, therapistCount = 0) => {
  const cityUrlSlug = city.toLowerCase().replace(/\s+/g, '-');
  const stateUrlSlug = state.toLowerCase().replace(/\s+/g, '-');
  
  return {
    title: `Therapists in ${city}, ${state} | Find Mental Health Professionals | TherapyList`,
    description: `Find licensed therapists in ${city}, ${state}. Browse ${therapistCount}+ mental health professionals accepting new patients. Book appointments online.`,
    keywords: `therapists ${city} ${state}, mental health ${city}, therapy ${city}, counseling ${city}, psychologist ${city}, psychiatrist ${city}`,
    canonical: `${baseUrl}/therapists/${stateUrlSlug}/${cityUrlSlug}`,
    ogTitle: `Licensed Therapists in ${city}, ${state}`,
    ogDescription: `Connect with licensed mental health professionals in ${city}, ${state}. Find the right therapist for your needs.`,
    additionalMeta: [
      { name: 'geo.region', content: `US-${getStateAbbreviation(state)}` },
      { name: 'geo.placename', content: `${city}, ${state}` },
      { property: 'og:locality', content: city },
      { property: 'og:region', content: state },
      { property: 'og:country-name', content: 'United States' }
    ]
  };
};

export const generateStateSEO = (state, therapistCount = 0) => {
  const stateUrlSlug = state.toLowerCase().replace(/\s+/g, '-');
  
  return {
    title: `Therapists in ${state} | Find Mental Health Professionals | TherapyList`,
    description: `Find licensed therapists throughout ${state}. Browse ${therapistCount}+ mental health professionals in major cities across ${state}.`,
    keywords: `therapists ${state}, mental health ${state}, therapy ${state}, counseling ${state}, psychology ${state}`,
    canonical: `${baseUrl}/therapists/${stateUrlSlug}`,
    ogTitle: `Licensed Therapists in ${state}`,
    ogDescription: `Connect with licensed mental health professionals throughout ${state}. Find therapists in your city.`
  };
};

export const generateSpecialtySEO = (specialty, location = '') => {
  const locationText = location ? ` in ${location}` : '';
  const specialtySlug = specialty.toLowerCase().replace(/\s+/g, '-');
  const locationSlug = location ? `/${location.toLowerCase().replace(/\s+/g, '-')}` : '';
  
  return {
    title: `${specialty} Therapists${locationText} | TherapyList`,
    description: `Find licensed ${specialty} therapists${locationText}. Connect with mental health professionals specializing in ${specialty.toLowerCase()}.`,
    keywords: `${specialty} therapist, ${specialty} therapy, ${specialty} counseling${location ? `, ${location}` : ''}`,
    canonical: `${baseUrl}/specialties/${specialtySlug}${locationSlug}`,
    ogTitle: `Licensed ${specialty} Therapists${locationText}`,
    ogDescription: `Connect with professional ${specialty} therapists${locationText}. Find specialized mental health care.`
  };
};

// Helper function to get state abbreviations for geo meta tags
function getStateAbbreviation(state) {
  const states = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
    'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
    'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
    'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
    'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
    'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
    'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
    'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
  };
  return states[state] || state.substring(0, 2).toUpperCase();
}

// Function to generate popular city combinations for a state (for building sitemaps)
export const generatePopularCitiesForState = (state) => {
  // This would typically come from your database
  // For now, here are some examples:
  const popularCities = {
    'california': ['los-angeles', 'san-francisco', 'san-diego', 'sacramento', 'oakland'],
    'texas': ['houston', 'dallas', 'austin', 'san-antonio', 'fort-worth'],
    'new-york': ['new-york-city', 'buffalo', 'rochester', 'yonkers', 'syracuse'],
    'florida': ['miami', 'orlando', 'tampa', 'jacksonville', 'tallahassee']
    // Add more states as needed
  };
  
  return popularCities[state.toLowerCase().replace(/\s+/g, '-')] || [];
}; 